// src/features/manual/utils/chapterBlocks.ts
//
// Turns a chapter's Markdown body into the blocks the page lays out, in reading order: ordinary
// text, and the staff blocks (`:::staff` ... `:::`) that get their own labelled box. The API only
// sends staff blocks to Roommatik staff; a customer's page also passes `includeStaff: false`, so a
// block that slipped through is dropped, never drawn. Headings keep their place in the text; the
// anchors the API lifted out come back through `headingAnchors`.

import type { ManualSection } from '../types';

export interface ManualBlock {
  kind: 'text' | 'staff';
  markdown: string;
}

export interface SplitOptions {
  includeStaff?: boolean;
}

const FENCE = /^\s*(```|~~~)/;
const STAFF_OPEN = /^:::staff$/i;

export const splitChapter = (body: string, { includeStaff = true }: SplitOptions = {}): ManualBlock[] => {
  const blocks: ManualBlock[] = [];
  let text: string[] = [];
  let staff: string[] | null = null;
  let inCode = false;

  const flushText = () => {
    const markdown = text.join('\n').trim();
    if (markdown) blocks.push({ kind: 'text', markdown });
    text = [];
  };

  for (const line of body.split(/\r?\n/)) {
    if (staff) {
      if (line.trim() === ':::') {
        const markdown = staff.join('\n').trim();
        if (includeStaff && markdown) blocks.push({ kind: 'staff', markdown });
        staff = null;
      } else {
        staff.push(line);
      }
      continue;
    }

    if (FENCE.test(line)) inCode = !inCode;
    if (!inCode && STAFF_OPEN.test(line.trim())) {
      flushText();
      staff = [];
      continue;
    }
    text.push(line);
  }

  // An unterminated staff block is dropped rather than rendered half-way.
  flushText();
  return blocks;
};

/** Heading text as the reader sees it: Markdown emphasis and code marks removed, spaces collapsed. */
export const plainHeading = (text: string): string => text.replace(/[*_`]/g, '').replace(/\s+/g, ' ').trim();

/** Heading text → anchor, for the headings the API reported with one. */
export const headingAnchors = (sections: ManualSection[]): Map<string, string> => {
  const anchors = new Map<string, string>();
  for (const section of sections) {
    const key = plainHeading(section.heading);
    if (!anchors.has(key)) anchors.set(key, section.anchor);
  }
  return anchors;
};
